import { Component, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { SaasService } from '../../Service/saas-service'; // Tres niveles de salida
import { CustomersService } from '../../Service/customers-service';
import { ToastService } from '../../Service/toast-service';    // Dos niveles de salida
import { UdcsService } from '../../Service/udcs-service'; // 🚀 IMPORTADO
import { UppercaseDirective } from '../../util/uppercase'; // 🚀 IMPORTADO
//import { filter } from 'rxjs/operators';
import { Router } from '@angular/router';

interface RegimenSAT {
  codigo: string;
  descripcion: string;
  tipo: 'FISICA' | 'MORAL';
}

@Component({
  selector: 'app-saas-company-add',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, UppercaseDirective],
  templateUrl: './saas-company-add.html',
  styleUrl: './saas-company-add.css',
})

export class SaasCompanyAdd implements OnInit {
  saasForm!: FormGroup;

// Dentro de tu clase SaasCompanyAdd:
tiposPersona: RegimenSAT[] = [
  { codigo: '601', descripcion: 'MORAL - General de Ley Personas Morales', tipo: 'MORAL' },
  { codigo: '603', descripcion: 'MORAL - Personas Morales con Fines no Lucrativos', tipo: 'MORAL' },
  { codigo: '620', descripcion: 'MORAL - Sociedad Cooperativa de Producción', tipo: 'MORAL' },
  { codigo: '623', descripcion: 'MORAL - Régimen Opcional para Grupos de Sociedades', tipo: 'MORAL' },
  { codigo: '624', descripcion: 'MORAL - Coordinados (Transporte)', tipo: 'MORAL' },
  { codigo: '605', descripcion: 'FISICA - Sueldos y Salarios', tipo: 'FISICA' },
  { codigo: '606', descripcion: 'FISICA - Arrendamiento', tipo: 'FISICA' },
  { codigo: '612', descripcion: 'FISICA - Actividad Empresarial y Profesional', tipo: 'FISICA' },
  { codigo: '625', descripcion: 'FISICA - Plataformas Tecnológicas', tipo: 'FISICA' },
  { codigo: '626', descripcion: 'FISICA - Régimen Simplificado de Confianza (RESICO)', tipo: 'FISICA' }
];

  // Reactividad e Integración Geográfica idéntica a CustomerAdd
  paises: any[] = [];
  estados: any[] = [];
  mostrarDesplegableEstado: boolean = true; 

  // 🚀 AGREGA ESTE GETTER AQUÍ
  get f() {
    return this.saasForm.controls;
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    //public dialogRef: MatDialogRef<SaasCompanyAdd>,
    private saasService: SaasService,
    private customerService: CustomersService,
    private udcsService: UdcsService, // 🚀 INYECTADO
    public toastService: ToastService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initForm();                     // 1. Construye el formulario limpio
    this.escucharErroresCorreo();         // 3. Escucha reactiva con Debounce para alertas flotantes de duplicidad
    this.escucharErroresRfc();
    this.cargarConfiguracionGeografica(); // 2. Consume servicios geográficos UDC y setea 'MX' por defecto
  }

  private initForm(): void {
    // Construcción del engranaje con validaciones rígidas empresariales
    this.saasForm = this.fb.group({
      nameAlpha: ['', [Validators.required, Validators.maxLength(255)]],
      //rfc: ['', [Validators.required, Validators.maxLength(50)]],

      rfc: [
        '', 
        [Validators.required, Validators.pattern('^[A-Z&Ññ]{3,4}[0-9]{6}[A-Z0-9]{3}$')], // ('^[A-Z&Ññ]{3,4}[0-9]{6}[A-Z0-9]{3}$')
        [this.validarRfcUnico.bind(this)]
      ],
      tax2: ['601', [Validators.required]], // Combo Régimen Empresa

      ownerRfc: [
        '', 
        [Validators.required, Validators.pattern('^[A-Z&Ññ]{3,4}[0-9]{6}[A-Z0-9]{3}$')], // ('^[A-Z&Ññ]{3,4}[0-9]{6}[A-Z0-9]{3}$')
        [this.validarRfcUnico.bind(this)]
      ],   
      ownerTax2: ['612', [Validators.required]], // Combo Régimen Representante   

      ownerName: ['', [Validators.required, Validators.maxLength(255)]],
      supportPhone: ['', [Validators.required, Validators.maxLength(50)]],
      
      // 🏛️ Homologación JDE Contable Estricta de 12 y 5 caracteres
      mcuBu: ['       1', [Validators.required, Validators.maxLength(12)]], // Alineado por defecto a la derecha
      ccco: ['AUTO', [Validators.required, Validators.maxLength(5)]],
      crdc: ['MXN', [Validators.required, Validators.maxLength(3)]],
      
      // 🎛️ Datos de la Cuenta de Administrador del Taller
      adminUser: ['', [Validators.required, Validators.maxLength(50)]],
      adminPassword: ['', [Validators.required, Validators.minLength(6)]],
      //email: ['', [Validators.required, Validators.email, Validators.maxLength(150)]], // 🚀 NUEVO CAMPO CON VALIDADOR
      
      // 🌟 VALIDADOR ASÍNCRONO INYECTADO DIRECTAMENTE AL NACIMIENTO DEL CAMPO
      email: [
        '', 
        [Validators.required, Validators.email, Validators.maxLength(150)],
        [this.validarEmailUnico.bind(this)]
      ], 

      adminAn8: [10000001, [Validators.required, Validators.min(100)]],

      // =========================================================================
      // 🚀 INYECCIÓN PARAMÉTRICA: CONTROLES LEGALES DE CONFORMIDAD JURÍDICA
      // Sincronizado al 100% con tu payload y semillero de Spring Boot.
      // =========================================================================
      tipoTaller: ['AMBOS', Validators.required],                  // MECANICO, LAMINADO, AMBOS
      numRegContAdhesion: ['0', [Validators.required, Validators.maxLength(100)]], // Registro Profeco
      tallerCuentaSeguro: ['NO', Validators.required],               // SI, NO
 
      calleNumero: ['', [Validators.required, Validators.maxLength(255)]],
      colonia: ['', [Validators.required, Validators.maxLength(255)]],
      codigoPostal: ['', [Validators.required, Validators.pattern('^[0-9]{5}$')]], // Validación estricta CP México
      delegacionMunicipio: ['', [Validators.required, Validators.maxLength(255)]],
      country: ['MX', [Validators.required]], // Coordinado para el combo
      estado: ['', [Validators.required, Validators.maxLength(255)]],
      ciudad: ['', [Validators.required, Validators.maxLength(255)]],
      // 🚀 Bootstrap Switch de Suscripción Válida
      enabled: [true]
    }, { 
      // 🚀 INYECCIÓN DE VALIDACIÓN DOBLE CRUZADA SIMULTÁNEA
      validators: [
        this.validadorRegimenEmpresa.bind(this),
        this.validadorRegimenRepresentante.bind(this)
      ]
    });
  }

  private validadorRegimenEmpresa(group: AbstractControl): ValidationErrors | null {
    const rfcCtrl = group.get('rfc');
    const tax2Ctrl = group.get('tax2');
    return this.ejecutarValidacionCruzada(rfcCtrl, tax2Ctrl);
  }

  private validadorRegimenRepresentante(group: AbstractControl): ValidationErrors | null {
    const rfcCtrl = group.get('ownerRfc');
    const tax2Ctrl = group.get('ownerTax2');
    return this.ejecutarValidacionCruzada(rfcCtrl, tax2Ctrl);
  }

  /**
   * Motor genérico reutilizable para validar congruencia RFC vs Régimen SAT
   */
  private ejecutarValidacionCruzada(rfcCtrl: AbstractControl | null, tax2Ctrl: AbstractControl | null): ValidationErrors | null {
    if (!rfcCtrl || !tax2Ctrl) return null;

    const rfcLimpio = (rfcCtrl.value || '').toString().trim().toUpperCase();
    const longitudRfc = rfcLimpio.length;
    
    if (rfcLimpio === 'XAXX010101000' || longitudRfc === 0) return null;

    const regimenInfo = this.tiposPersona.find(r => r.codigo === tax2Ctrl.value);
    if (!regimenInfo) return null;

    let error: ValidationErrors | null = null;

    if (longitudRfc === 12 && regimenInfo.tipo === 'FISICA') {
      error = { rfcRegimenInvalido: 'Discrepancia: RFC de 12 dígitos requiere un Régimen de Persona Moral.' };
    } else if (longitudRfc === 13 && regimenInfo.tipo === 'MORAL') {
      error = { rfcRegimenInvalido: 'Discrepancia: RFC de 13 dígitos requiere un Régimen de Persona Física.' };
    } else if (longitudRfc !== 12 && longitudRfc !== 13) {
      error = { rfcRegimenInvalido: 'El RFC debe medir exactamente 12 o 13 caracteres.' };
    }

    if (error) {
      rfcCtrl.setErrors({ ...rfcCtrl.errors, ...error });
      tax2Ctrl.setErrors({ ...tax2Ctrl.errors, ...error });
    } else {
      if (rfcCtrl.errors) {
        const { rfcRegimenInvalido, ...rest } = rfcCtrl.errors;
        rfcCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
      if (tax2Ctrl.errors) {
        const { rfcRegimenInvalido, ...rest } = tax2Ctrl.errors;
        tax2Ctrl.setErrors(Object.keys(rest).length ? rest : null);
      }
    }
    return error;
  }

  // Agrega este método en tu componente .ts para bloquear letras al escribir
  soloNumeros(event: KeyboardEvent): boolean {
    const charCode = event.key;
    // Si la tecla no es un número del 0 al 9, cancela la pulsación
    if (!/^[0-9]$/.test(charCode)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
  
  // 🌟 CONSUMO DINÁMICO IDÉNTICO A CUSTOMER ADD
  cargarConfiguracionGeografica(): void {
    this.udcsService.getPaisesMundo().subscribe({
      next: (data) => {
        this.paises = data; 
        console.log('>>> Catálogo de Países cargado con éxito en SaaS:', this.paises);

        this.cdr.detectChanges(); 
        
        // Inicializamos por defecto en México asignando 'MX'
        this.saasForm.get('country')?.setValue('MX');
        this.mostrarDesplegableEstado = true;
        this.cargarEstadosCascada('MX');
        
        this.cdr.detectChanges(); 
      },
      error: (err) => console.error('Error al recuperar países en SaaS:', err)
    });
  }

  // 🌟 ESCUCHA EN EL HTML EL CAMBIO DE PAÍS
  onPaisCambiado(event: any): void {
    const codigoPais = event.target.value;
    const estadoControl = this.saasForm.get('estado');

    if (codigoPais === 'MX' || codigoPais === 'US') {
      this.mostrarDesplegableEstado = true;
      this.cargarEstadosCascada(codigoPais);
    } else {
      this.mostrarDesplegableEstado = false;
      this.estados = [];
      estadoControl?.setValue(''); // Libera el campo para captura manual libre
    }
    this.cdr.detectChanges();
  }

  private cargarEstadosCascada(codigoPais: string): void {
    this.udcsService.getEstadosPorPais(codigoPais).subscribe({
      next: (data) => {
        this.estados = data;
        this.saasForm.get('estado')?.setValue(''); // Limpia selección del país anterior
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error al recuperar estados en cascada para SaaS:', err)
    });
  }

  private validarRfcUnico(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value || control.value.trim() === '') return of(null);
    const rfcLimpio = control.value.trim().toUpperCase();
    if (rfcLimpio === 'XAXX010101000') return of(null);

    return this.customerService.verificarRfcDuplicadoGlobal(rfcLimpio).pipe(
      map((existe: boolean) => existe ? { rfcDuplicado: true } : null),
      catchError(() => of(null)) // Protección si la red parpadea
    );
  }

  /**
   * Validador asíncrono para verificar correos repetidos en caliente en el ecosistema SaaS
   */
  private validarEmailUnico(control: AbstractControl): Observable<ValidationErrors | null> {

    if (!control.value || control.value.trim() === '') {
      return of(null); 
    }

    // 💡 SOLUCIÓN: Convertimos a MAYÚSCULAS para que haga match con tu registro 'TALA@TALLER.COM'
    const emailLimpio = control.value.trim().toUpperCase();

    // Invocación a tu pasarela HTTP de SaaS
    return this.customerService.verificarCorreoDuplicadoGlobal(emailLimpio).pipe(
    map((existe: boolean) => existe ? { correoDuplicado: true } : null),
    catchError(() => of(null))
    );
  }

/*   private escucharErroresRfc(): void {
    const rfcControl = this.saasForm.get('rfc');
    if (rfcControl) {
      // 💡 SOLUCIÓN: Escuchamos statusChanges para reaccionar cuando el validador asíncrono termine
      rfcControl.statusChanges.pipe( // valueChanges
        //filter(status => status === 'INVALID' || status === 'VALID'),
        debounceTime(150), // 600 // Un debounce corto para evitar doble ejecución visual
        //distinctUntilChanged()
      ).subscribe((status) => {
        // 🚀 ADUANA MULTI-TENANT: Si tiene el error de duplicidad y el valor NO es el genérico,
        // levantamos el Toast rojo inmediatamente, sin importar si está en carga rápida o detallada.

        // 📊 LOG DE SEGUIMIENTO EN PANTALLA
        console.log('--- REEVALUANDO ERRORES DE RFC ---');
        console.log('¿Tiene error de duplicidad?:', rfcControl.hasError('rfcDuplicado'));

        // 📊 LOG 1: Ver qué estatus y qué errores tiene el control en tiempo real
        console.log('--- MONITOREO RFC ---');
        console.log('Estatus actual del control:', status);
        console.log('Errores activos en el control:', rfcControl.errors);
        console.log('Valor del RFC:', rfcControl.value);

        //if (rfcControl.hasError('rfcDuplicado') && rfcControl.value?.toUpperCase() !== 'XAXX010101000') {
        if (rfcControl.hasError('rfcDuplicado') && rfcControl.value?.toUpperCase() !== 'XAXX010101000') {

          console.log('🛑 [TOAST DETONADO] El RFC ya existe de forma global.');
          console.log('🛑 Disparando Toast de RFC Duplicado...'); // Agrega este log para validar en consola
          console.log('🛑 ¡PERFECTO! La condición se cumplió. Intentando disparar Toast...');

          setTimeout(() => {
            this.toastService.showDanger(
              'Bloqueo por Duplicidad Fiscal',
              `El RFC [${rfcControl.value.toUpperCase()}] ya pertenece a otro cliente registrado en esta compañía.`
            );
            rfcControl.markAsTouched();
            this.cdr.detectChanges(); 
          }, 0);
        }
      });
    }
  } */


  private escucharErroresRfc(): void {
    this.saasForm.valueChanges.pipe(
      debounceTime(600),
      distinctUntilChanged((prev, curr) => 
        prev.rfc === curr.rfc && prev.tax2 === curr.tax2 && 
        prev.ownerRfc === curr.ownerRfc && prev.ownerTax2 === curr.ownerTax2
      )
    ).subscribe(() => {
      const rfcEmpresa = this.saasForm.get('rfc');
      const rfcRep = this.saasForm.get('ownerRfc');

      if (rfcEmpresa?.hasError('rfcRegimenInvalido')) {
        this.toastService.showDanger('Incongruencia SAT: Empresa', `⚠️ ${rfcEmpresa.getError('rfcRegimenInvalido')}`);
      }
      if (rfcRep?.hasError('rfcRegimenInvalido')) {
        this.toastService.showDanger('Incongruencia SAT: Representante', `⚠️ ${rfcRep.getError('rfcRegimenInvalido')}`);
      }
    });
  }
    

  /**
   * Monitorea la escritura con retraso inteligente para disparar el Toast flotante rojo
   */
  private escucharErroresCorreo(): void {
    const emailControl = this.saasForm.get('email');
    if (emailControl) {
      emailControl.statusChanges.pipe( // valueChanges
        //filter(status => status === 'INVALID' || status === 'VALID'),
        debounceTime(150),   // 600     
        //distinctUntilChanged()    
      ).subscribe((status) => {

        console.log('Estatus actual del control:', status);

        this.ngZone.run(() => {
          if (emailControl.hasError('correoDuplicado')) {
            console.log('🛑 Disparando Toast de email Duplicado...'); // Agrega este log para validar en consola
            this.toastService.showDanger(
              'Bloqueo de Registro SaaS',
              `El correo corporativo [${emailControl.value.toUpperCase()}] ya se encuentra asignado a otra compañía inquilina activa.`
            );
            emailControl.markAsTouched();
          }
          this.cdr.detectChanges(); 
        });   

      });
    }
  }



  onSubmit(): void {
    if (this.saasForm.invalid) {
      this.saasForm.markAllAsTouched();
      this.toastService.showDanger('Formulario Incompleto', 'Por favor, revise los campos obligatorios marcados en rojo.');
      return;
    }

    const rawForm = this.saasForm.getRawValue();

    // 🛡️ ARMADO TRANSACCIONAL ATÓMICO (SANITIZADO EN MAYÚSCULAS)
    const payloadAprovisionamiento = {
      nameAlpha: rawForm.nameAlpha.trim().toUpperCase(),
      taxId: rawForm.rfc.trim().toUpperCase(),
      tax2: rawForm.tax2, // Enviado directamente desde el combo de la Empresa

      ownerRfc: rawForm.ownerRfc.trim().toUpperCase(),
      ownerTax2: rawForm.ownerTax2, // Enviado directamente desde el combo del Representante
      
      ownerName: rawForm.ownerName.trim().toUpperCase(),
      supportPhone: rawForm.supportPhone.trim(),
      
      // Estandarización de Negocio Contable
      mcuBu: rawForm.mcuBu.toUpperCase(),
      ccco: rawForm.ccco.trim(),
      crdc: rawForm.crdc.trim().toUpperCase(),
      enabled: rawForm.enabled,
      
      // Paquete complementario para la inyección simultánea del Admin del taller
      adminUser: rawForm.adminUser.trim().toLowerCase(), // Login IDs estrictamente en minúsculas
      adminPassword: rawForm.adminPassword,
      //adminAn8: parseInt(rawForm.adminAn8, 10),
      // 🛡️ SANEADO: String(rawForm.adminAn8) previene fallos de tipado estricto en el compilador de Angular

      email: rawForm.email.trim().toUpperCase(), // 🚀 SANEADO EN MINÚSCULAS PARA LA BASE DE DATOS

      adminAn8: parseInt(String(rawForm.adminAn8), 10),

      // =========================================================================
      // 🚀 ENVÍO EN EL CABLE: MAPEADO DE VARIABLES EXCLUSIVAS DE INFRAESTRUCTURA
      // Pasa limpio por la rampa HTTP directo a SaasProvisionService.java
      // =========================================================================
      // Variables operativas legales
      tipoTaller: rawForm.tipoTaller.trim().toUpperCase(),
      numRegContAdhesion: rawForm.numRegContAdhesion.trim().toUpperCase(),
      tallerCuentaSeguro: rawForm.tallerCuentaSeguro.trim().toUpperCase(),

      // 📍 Inyección de Dirección para la Solución A (Customers)
      calleNumero: rawForm.calleNumero ? rawForm.calleNumero.trim().toUpperCase() : '',
      colonia: rawForm.colonia ? rawForm.colonia.trim().toUpperCase() : '',
      codigoPostal: rawForm.codigoPostal ? rawForm.codigoPostal.trim() : '',
      delegacionMunicipio: rawForm.delegacionMunicipio ? rawForm.delegacionMunicipio.trim().toUpperCase() : '',
      estado: rawForm.estado ? rawForm.estado.trim().toUpperCase() : '',
      country: rawForm.country ? rawForm.country.trim().toUpperCase() : '',
      ciudad: rawForm.ciudad ? rawForm.ciudad.trim().toUpperCase() : ''
    };

    console.log('Despachando aprovisionamiento masivo a Spring Boot:', payloadAprovisionamiento);

    // Invocamos tu servicio HTTP global
    this.saasService.crearNuevaCompania(payloadAprovisionamiento).subscribe({
      next: (res) => {
        // Cerramos la ventana modal regresando el objeto exitoso para refrescar el grid anterior

        //this.dialogRef.close(res);
        // 💡 ANTES: this.dialogRef.close(res);

        // 💡 AHORA: Lanzamos el Toast de éxito aquí mismo y navegamos de vuelta a la lista
        this.toastService.showSuccess(
          'Despliegue Exitoso', 
          `El taller [${payloadAprovisionamiento.nameAlpha}] ha sido provisionado de forma atómica en MySQL.`
        );

        this.router.navigate(['/admin/saas']); // Redirección automática        

      },
      error: (err) => {
        console.error('Error fatal de provisionamiento SaaS:', err);
        this.toastService.showDanger(
          'Fallo de Despliegue',
          err.error?.error || 'El RFC o el Código de Compañía ya se encuentran registrados.'
        );
        this.cdr.detectChanges();
      }
    });
  }


  
}
